/**
 * C.2 — Admin UI: /admin/portals.
 *
 * Clerk-gated landing page for the portal operator. Lists every
 * `tenantBranding` row + a per-portal subscription health pill, plus the
 * F12 cross-tenant health summary at the top.
 *
 * Operator actions are intentionally minimal in this slice:
 *   - View portal list + branding.
 *   - View subscription health per portal.
 *   - (Future C.2.b) Force-renew button — calls a Convex action that
 *     POSTs the subscriptionManager once for that row.
 *
 * Auth model:
 *   - Clerk `useUser` must return a signed-in user. Otherwise show a
 *     friendly "Sign in to continue" hint.
 *   - Convex queries (`tenantBranding.listAll`, sub health summary)
 *     gate themselves via `requireAdmin` on the server side; non-admin
 *     users see error-rendered states, never sensitive content.
 *
 * F17b's scraper wizard (`/admin/portals/new`) lands in C.3 as a child
 * route under this page.
 */

import { createFileRoute, useRouterState, Outlet } from '@tanstack/react-router';
import { useUser } from '@clerk/react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useRepancyRole } from '../../lib/auth/useRepancyRole';

export const Route = createFileRoute('/admin/portals')({
  component: AdminPortalsPage,
});

interface TenantRow {
  _id: string;
  clientSlug: string;
  displayName: string;
  primaryColor: string;
  practiceArea: 'corporate-ma' | 'litigation-disputes';
  theme: string;
  lockedTenantId: string;
  lockedSiteIds: string[];
  firmSkillsSiteId?: string;
}

function HealthBadge({ state }: { state: 'green' | 'amber' | 'red' | 'loading' }) {
  const cls =
    state === 'green' ? 'bg-green-700 text-white'
      : state === 'amber' ? 'bg-amber-500 text-white'
      : state === 'red' ? 'bg-red-700 text-white'
      : 'bg-gray-400 text-gray-800';
  const label = state === 'loading' ? '…' : state.toUpperCase();
  return (
    <span className={`inline-block rounded-sm ${cls} px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide`}>
      {label}
    </span>
  );
}

function AdminPortalsPage() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { role } = useRepancyRole();
  // B.SMOKE.8 fix — when the URL is a CHILD route (/admin/portals/new),
  // don't render this list page at all; the child route renders itself.
  // Without this, TanStack Router renders both the parent and the
  // child, and the parent's admin-gated query throws for non-admin
  // users (operators included — they're not `repancy_admin`).
  const routerState = useRouterState();
  const isChildRoute = routerState.location.pathname !== '/admin/portals';
  if (isChildRoute) {
    return <Outlet />;
  }
  // Skip admin-gated queries unless we have the admin role; otherwise
  // the requireAdmin throw fills the page with an error overlay.
  const isAdmin = isLoaded && isSignedIn && role === 'admin';
  const portals = useQuery(
    api.tenantBranding.listAll,
    isAdmin ? {} : 'skip',
  ) as TenantRow[] | undefined;
  const subHealth = useQuery(
    api.sharepointSubscriptions.healthSummary,
    isAdmin ? {} : 'skip',
  );

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-white p-6 text-black">
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-white p-6 text-black">
        <h1 className="mb-4 text-xl font-bold">Repancy admin</h1>
        <p className="text-sm">
          Sign in with a Clerk account to continue. Portal operations require
          the <code>repancy_admin</code> role.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={testIds.adminPortals.root}
      className="min-h-screen bg-white p-6 text-black"
    >
      <header className="mb-6 flex items-center justify-between border-b border-black pb-3">
        <div>
          <h1 className="text-xl font-bold">Repancy admin · Portals</h1>
          <p className="text-xs text-gray-700">
            Signed in as {user?.primaryEmailAddress?.emailAddress ?? user?.id}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-700">Subscriptions health</span>
          <HealthBadge state={subHealth?.state ?? 'loading'} />
        </div>
      </header>

      {subHealth !== undefined && (
        <section
          data-testid={testIds.adminPortals.subHealth}
          className="mb-6 border border-gray-300 bg-gray-50 px-3 py-2 text-xs"
        >
          <div className="flex gap-4">
            <span>Total: <strong>{subHealth.totalSubscriptions}</strong></span>
            <span>Failures (24h): <strong>{subHealth.recentFailures}</strong></span>
            <span>Expiring soon: <strong>{subHealth.upcomingExpiry}</strong></span>
            <span>Silent &gt;24h: <strong>{subHealth.staleNotifications}</strong></span>
            <span>Expired: <strong>{subHealth.expired}</strong></span>
          </div>
        </section>
      )}

      <section data-testid={testIds.adminPortals.list}>
        <h2 className="mb-3 text-lg font-bold">Portals</h2>
        {portals === undefined ? (
          <p className="text-sm text-gray-700">Loading portals…</p>
        ) : portals.length === 0 ? (
          <p className="text-sm text-gray-700">
            No portals yet. Run <code>scripts/createPortal.ts</code> or wait for
            the C.3 wizard.
          </p>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className="border-b-2 border-black text-left text-xs uppercase">
              <tr>
                <th className="py-1.5 pr-2">Slug</th>
                <th className="py-1.5 pr-2">Display name</th>
                <th className="py-1.5 pr-2">Practice</th>
                <th className="py-1.5 pr-2">Theme</th>
                <th className="py-1.5 pr-2">Tenant</th>
                <th className="py-1.5 pr-2">Sites</th>
              </tr>
            </thead>
            <tbody>
              {portals.map((p) => (
                <PortalRow key={p._id} portal={p} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function PortalRow({ portal }: { portal: TenantRow }) {
  return (
    <tr className="border-b border-gray-200" data-testid={testIds.adminPortals.portalRow(portal.clientSlug)}>
      <td className="py-1.5 pr-2">
        <code>{portal.clientSlug}</code>
      </td>
      <td className="py-1.5 pr-2">{portal.displayName}</td>
      <td className="py-1.5 pr-2">{portal.practiceArea}</td>
      <td className="py-1.5 pr-2">{portal.theme}</td>
      <td className="py-1.5 pr-2 font-mono text-[11px]">
        {portal.lockedTenantId.slice(0, 8)}…
      </td>
      <td className="py-1.5 pr-2">
        {portal.lockedSiteIds.length}
      </td>
    </tr>
  );
}
